import {
    Box,
    FormControlLabel,
    makeStyles,
    Switch,
    TextField,
} from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useCompanyContext } from './CompanyContext';
import { CompanySearchField } from './CompanySearchField';
import type { FormField } from '../../interfaces';
import { getLocalizedLabel } from '../../utils';
import { getFormFieldStyles } from '@klp/formgenerator/formFieldStyles';
import type { Company } from '../../services/rtkApi';
import { MANUAL_ORG_NUMBER } from '.';

const useStyles = makeStyles((theme) => ({
    companySearchBox: {
        marginBottom: theme.spacing(2),
    },
}));

export type CompanySelectorFormProps = {
    error: string | undefined;
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
    defaultValue: Company['organisasjonsnummer'];
    initialManualName?: string;
};

export const CompanySelectorForm: React.FC<CompanySelectorFormProps> = ({
    error,
    field,
    isReadOnly,
    isRequired,
    defaultValue,
    initialManualName,
}) => {
    const classes = useStyles();
    const formFieldClasses = getFormFieldStyles();
    const { t } = useTranslation();
    const { selectedCompany, setSelectedCompany, setCompanyClear } =
        useCompanyContext();

    const [isManualMode, setIsManualMode] = useState(
        defaultValue === MANUAL_ORG_NUMBER,
    );
    const [manualName, setManualName] = useState(initialManualName ?? '');

    const handleCompanySelection = (newValue: Company | null) => {
        setSelectedCompany(newValue);
    };

    const handleToggleMode = () => {
        setCompanyClear();
        setManualName('');
        setIsManualMode((prev) => !prev);
    };

    const handleManualNameChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const name = event.target.value;
        setManualName(name);
        setSelectedCompany({
            navn: name,
            organisasjonsnummer: MANUAL_ORG_NUMBER,
            konkurs: null,
        });
    };

    const label = getLocalizedLabel(
        `project.formFields.${field.id}`,
        field.label,
        field.id,
    );

    return (
        <Box className={classes.companySearchBox}>
            {!isReadOnly && (
                <FormControlLabel
                    data-testid="manual-mode-toggle"
                    control={
                        <Switch
                            checked={isManualMode}
                            onChange={handleToggleMode}
                            color="primary"
                        />
                    }
                    label={t('project.formFields.manuellRegistrering')}
                />
            )}
            {isManualMode ? (
                <TextField
                    id={field.id}
                    label={t('project.formFields.firmanavnManuell')}
                    value={manualName}
                    onChange={handleManualNameChange}
                    variant="outlined"
                    fullWidth={true}
                    required={isRequired}
                    disabled={isReadOnly}
                    className={classNames({
                        [formFieldClasses.editableField]: !isReadOnly,
                        [formFieldClasses.readOnlyField]: isReadOnly,
                    })}
                    error={Boolean(error)}
                    helperText={error ?? ''}
                    inputProps={{
                        'data-testid': 'manual-entry-field',
                    }}
                />
            ) : (
                <CompanySearchField
                    id={field.id}
                    label={label}
                    selectedCompany={selectedCompany}
                    error={error}
                    isReadOnly={isReadOnly}
                    isRequired={isRequired}
                    defaultValue={defaultValue}
                    onChange={handleCompanySelection}
                    onClear={setCompanyClear}
                />
            )}
        </Box>
    );
};
